import type { BeforeSendFn, CaptureResult } from 'posthog-js'

export const DEFAULT_POSTHOG_API_HOST = 'https://us.i.posthog.com'
export const POSTHOG_UI_HOST = 'https://us.posthog.com'

/** PostHog's auto-emitted feature-flag exposure event. */
const FEATURE_FLAG_CALLED_EVENT = '$feature_flag_called'
/** Property on `$feature_flag_called` holding the flag key. */
const FEATURE_FLAG_KEY_PROPERTY = '$feature_flag'

/**
 * Non-experiment flag keys whose `$feature_flag_called` exposure event we
 * deliberately KEEP, regardless of the dynamic running-experiment list. These
 * are explicit product decisions, not experiments — so they can't be derived
 * from the PostHog experiments API and live here instead. Empty today: its
 * only entry, `chat-dupe-maxx`, was retired once it reached everyone.
 *
 * Running-experiment flags are NOT listed here: they're fetched dynamically
 * server-side (`posthogExperimentFlags.ts`) and merged with this set in the
 * middleware before being handed to the client. That keeps the source of truth
 * in PostHog instead of drifting in code.
 */
export const ALWAYS_KEEP_EXPOSURE_FLAG_KEYS: readonly string[] = []

/**
 * How the client should treat `$feature_flag_called` events:
 *   - `keep_all`: send every flag-call event (safe default when we couldn't
 *     determine the running-experiment list — never sacrifice experiment
 *     exposure for cost).
 *   - `allowlist`: drop every flag-call event EXCEPT for `keys` (running
 *     experiments + {@link ALWAYS_KEEP_EXPOSURE_FLAG_KEYS}).
 */
export type ExposureFilterConfig =
  | { mode: 'keep_all' }
  | { mode: 'allowlist'; keys: ReadonlySet<string> }

/**
 * Parse the server-set `__dupe_exposure_flags` cookie into an
 * {@link ExposureFilterConfig}. A missing or malformed cookie falls back to
 * `keep_all`, so a *bad* cookie can never silently drop experiment exposures.
 *
 * Note this only guards malformed input — it does not make allowlists fresh. A
 * syntactically valid `allowlist` cookie is only as current as the middleware
 * refresh cadence (server cache TTL + the next request that re-runs middleware,
 * picked up by an already-open tab only on its next full page load). So a
 * just-launched experiment can have its exposures dropped until the allowlist
 * catches up. See `posthogExperimentFlags.ts` / `docs/references/posthog-cost-reduction.md`.
 */
export function parseExposureFlagsCookie(
  raw: string | null | undefined
): ExposureFilterConfig {
  if (!raw) return { mode: 'keep_all' }
  try {
    const parsed = JSON.parse(raw) as { mode?: unknown; keys?: unknown }
    if (parsed?.mode === 'allowlist' && Array.isArray(parsed.keys)) {
      return {
        mode: 'allowlist',
        keys: new Set(
          parsed.keys.filter((k): k is string => typeof k === 'string')
        ),
      }
    }
    return { mode: 'keep_all' }
  } catch {
    return { mode: 'keep_all' }
  }
}

/**
 * Build the posthog-js `before_send` hook (supported since posthog-js 1.187.0)
 * that drops the high-volume `$feature_flag_called` event for non-exposure
 * flags. `$feature_flag_called` was our single most expensive event (~36% of
 * all billed events, ~16M/mo on web). It's billed on the product-analytics
 * event-ingestion line (plus the identified add-on when attached to a person) —
 * NOT the feature-flags line, which PostHog bills separately by `/flags`
 * evaluation-request volume and is unaffected by this event. Suppressing it
 * therefore cuts the analytics line only.
 *
 * This only suppresses the *telemetry event* — flag evaluation and flag values
 * are unaffected, so feature gating and experiment *bucketing* keep working.
 * Exposure-critical flags keep their event so experiment analysis is unchanged.
 *
 * PostHog counts a user as exposed to an experiment only when it receives a
 * `$feature_flag_called` event for the experiment's flag (unless the experiment
 * defines a custom exposure event), so the allowlist must contain every running
 * experiment's flag — which is why it's sourced dynamically from PostHog rather
 * than hardcoded.
 */
export function makeFeatureFlagCalledFilter(
  config: ExposureFilterConfig
): BeforeSendFn {
  return (event: CaptureResult | null): CaptureResult | null => {
    if (!event) return null
    if (event.event !== FEATURE_FLAG_CALLED_EVENT) return event
    // Unknown experiment list → keep every exposure event (safe).
    if (config.mode === 'keep_all') return event

    const flagKey = event.properties?.[FEATURE_FLAG_KEY_PROPERTY]
    if (typeof flagKey === 'string' && config.keys.has(flagKey)) {
      return event
    }

    // Non-allowlisted flag exposure → drop it (do not bill it).
    return null
  }
}

/** posthog-js exception-autocapture event name. */
const EXCEPTION_EVENT = '$exception'

/**
 * Cloudflare Zaraz's edge-injected tag-manager script lives under this path
 * (`/cdn-cgi/zaraz/s.js`). It's served from the Cloudflare zone, not our
 * bundle, so its runtime errors are not dupe.com bugs.
 */
const ZARAZ_SOURCE_FRAGMENT = '/cdn-cgi/zaraz/'

function stringIncludesZaraz(value: unknown): boolean {
  return typeof value === 'string' && value.includes(ZARAZ_SOURCE_FRAGMENT)
}

/**
 * True when a `$exception` event originates from Cloudflare Zaraz — either the
 * legacy top-level `$exception_source` URL or any stack-trace frame filename
 * under `/cdn-cgi/zaraz/`.
 */
function isZarazException(event: CaptureResult): boolean {
  const props = event.properties as Record<string, unknown> | undefined
  if (!props) return false

  if (stringIncludesZaraz(props.$exception_source)) return true

  const list = props.$exception_list
  if (Array.isArray(list)) {
    for (const item of list) {
      const frames = (item as { stacktrace?: { frames?: unknown } })?.stacktrace
        ?.frames
      if (!Array.isArray(frames)) continue
      for (const frame of frames) {
        const f = frame as { filename?: unknown; source?: unknown }
        if (
          stringIncludesZaraz(f?.filename) ||
          stringIncludesZaraz(f?.source)
        ) {
          return true
        }
      }
    }
  }
  return false
}

/**
 * `before_send` hook that drops `$exception` events originating from
 * Cloudflare Zaraz's edge-injected tag-manager script (`/cdn-cgi/zaraz/s.js`).
 *
 * posthog-js exception autocapture (enabled via `opt_in_site_apps`) catches
 * `window.onerror` from third-party scripts too, so Zaraz's errors would
 * otherwise surface in our error tracking as dupe.com bugs. This is purely
 * Cloudflare-zone code we don't ship or control. Non-exception events and
 * exceptions from our own code pass through untouched.
 */
export function dropZarazExceptionEvents(
  event: CaptureResult | null
): CaptureResult | null {
  if (!event) return null
  if (event.event !== EXCEPTION_EVENT) return event
  return isZarazException(event) ? null : event
}

/**
 * Passive, SDK-emitted page events. These are captured by posthog-js on its own
 * from navigation/lifecycle — nobody calls `capture()` for them — so dropping
 * them on a path costs no deliberate instrumentation.
 *
 * Deliberate `capture()` calls are NOT listed and keep firing on interstitials.
 * `Redirect Interstitial Failed` in particular is the alert signal postmortem
 * 2026-06-09 required, and must survive this filter.
 */
const PASSIVE_PAGE_EVENTS: ReadonlySet<string> = new Set([
  '$pageview',
  '$pageleave',
  '$web_vitals',
])

/**
 * The affiliate redirect interstitial: it renders a spinner, resolves an
 * affiliate link, and `window.location.replace()`s away. Nobody reads it.
 * Covers both `/redirect?link=…` and the tokenized `/redirect/:token`.
 *
 * Deliberately `/redirect` ONLY. `/link` looks like a sibling — the same
 * middleware branch builds it, from `dupe.com/s/<url>` — but it is not an
 * interstitial: `pages/link.tsx` mints a short URL and renders it as a
 * copy-to-clipboard button (plus a "Copied!" confirmation), so a human lands
 * there and stays. Its pageview is a real visit and must keep counting.
 */
const INTERSTITIAL_PATH_PREFIXES: readonly string[] = ['/redirect']

/** True for `/redirect` and `/redirect/:token` — but not `/redirects-foo` or `/link`. */
export function isInterstitialPathname(pathname: string): boolean {
  return INTERSTITIAL_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  )
}

/**
 * Read the event's pathname. posthog-js stamps `$pathname` on page events, but
 * fall back to parsing `$current_url` so a missing `$pathname` can't silently
 * reopen the hole.
 */
function readEventPathname(
  properties: Record<string, unknown> | undefined
): string | null {
  const pathname = properties?.$pathname
  if (typeof pathname === 'string' && pathname.length > 0) return pathname

  const currentUrl = properties?.$current_url
  if (typeof currentUrl !== 'string' || currentUrl.length === 0) return null
  try {
    return new URL(currentUrl).pathname
  } catch {
    return null
  }
}

/**
 * `before_send` hook that drops passive page events on the affiliate redirect
 * interstitials — they are a redirect hop, not a page, and should never have
 * minted a "visitor".
 *
 * Why this exists — the 8/15 "dupe rate drop"
 * (https://dupedotcom.slack.com/archives/C0208V6Q10C/p1786976225763239):
 * from 02:00 ET on
 * 2026-08-15 a scraper began harvesting `/redirect?link=…&src=…` hrefs out of
 * our server-rendered result cards and loading each one once. Every hit fired a
 * `$pageview` under a fresh anonymous person ID, adding ~5,500/day to the
 * visitor count — which pushed the unique dupe rate from ~55% to ~45% on paper
 * while the numerator never moved (12,976 → 12,997 dupers Fri → Sat). Two
 * independent sources confirmed no real traffic arrived: Plausible (which
 * filters bots) stayed flat, and affiliate click/resolution records stayed flat,
 * so nothing forged reached CJ, Connexity or adMarketplace.
 *
 * Neither existing gate catches it. PostHog's own bot detection flagged only 31
 * of ~5,800 hits, and the `middleware.ts` session filter is explicitly a
 * document-vs-non-document filter, not a bot filter — a crawler issuing
 * `Accept: text/html` looks exactly like a navigation. `robots.txt` does
 * disallow `/redirect`; this scraper ignores it.
 *
 * Suppressing here — rather than blocking traffic or patching each dashboard —
 * fixes the metric at the source and makes it permanently immune to the next
 * scraper, because it removes the interstitial's claim to being a page at all.
 * It is not a substitute for an edge rate-limit; it removes the *measurement*
 * damage, not the requests.
 *
 * Nothing is lost by dropping these: `Page Redirect` is captured SERVER-side in
 * `apps/api/src/controllers/redirect.ts`, so it counts only hops that actually
 * reached the resolver. That event stayed flat right through the surge
 * (49,615 → 51,303 on 8/15) — the bots load the HTML and fire the instant
 * `$pageview`, then abandon before the ~1s resolver round-trip completes. It is
 * the authoritative redirect signal and is bot-immune by construction.
 *
 * Two side effects, both intended:
 *   - Real visitors whose ONLY pageview was an interstitial (arriving straight
 *     from a Similar Finds email link, say) stop being counted: 677 of 23,779
 *     visitors, ~2.8%, on the clean 8/14 baseline. They could never have
 *     performed a dupe, so a site-engagement denominator is better without
 *     them — but it does put a one-time step UP in the dupe rate.
 *   - Two legacy insights read redirect pageviews and will go quiet:
 *     "Similar Finds Email Sent -> Redirect Page View" (`UNTqYyq5`) and
 *     "Affiliate Redirect Page unique users by event's Device Type"
 *     (`wi5ral5F`). Both re-express on `Page Redirect`, which is strictly
 *     better — it never counted the bots in the first place.
 */
export function dropInterstitialPageEvents(
  event: CaptureResult | null
): CaptureResult | null {
  if (!event) return null
  if (!PASSIVE_PAGE_EVENTS.has(event.event)) return event

  const pathname = readEventPathname(
    event.properties as Record<string, unknown> | undefined
  )
  if (!pathname) return event

  return isInterstitialPathname(pathname) ? null : event
}

interface ResolveBrowserPostHogApiHostOptions {
  configuredApiHost?: string
  origin?: string
}

export function resolveBrowserPostHogApiHost({
  configuredApiHost = process.env.NEXT_PUBLIC_POSTHOG_HOST,
  origin = typeof window !== 'undefined' ? window.location.origin : undefined,
}: ResolveBrowserPostHogApiHostOptions = {}): string {
  const apiHost = configuredApiHost || DEFAULT_POSTHOG_API_HOST

  // PostHog's toolbar rejects relative hosts. Keep proxied ingestion
  // first-party while giving the toolbar an absolute API URL.
  if (apiHost.startsWith('/') && origin) {
    return `${origin}${apiHost}`
  }

  return apiHost
}
